<script setup lang="ts">
interface SolfacilAddress {
  zipcode?: string | null
  street?: string | null
  number?: string | null
  complement?: string | null
  neighborhood?: string | null
  city?: string | null
  uf?: string | null
}

interface SolfacilData {
  document?: string | null
  corporate_name?: string | null
  municipal_registration?: string | null
  main_activity?: string | null
  address?: SolfacilAddress | null
  email?: string | null
}

const props = defineProps<{
  solfacilData?: SolfacilData | null
  commissionValue?: number | null
}>()
const { t } = useI18n()
const { formatMoney } = useMoney()
const titleId = `commission-invoice-info-title-${useId()}`

const EMPTY_VALUE = '-'

function toText(value: string | number | null | undefined): string {
  return value == null ? '' : String(value).trim()
}

function orEmptyValue(value: string | number | null | undefined): string {
  return toText(value) || EMPTY_VALUE
}

function formatCommissionValue(value: number | null | undefined): string {
  return typeof value === 'number' && Number.isFinite(value) ? formatMoney(value) : EMPTY_VALUE
}

function formatAddress(address: SolfacilAddress | null | undefined): string {
  if (!address)
    return EMPTY_VALUE

  const street = toText(address.street)
  const number = toText(address.number)
  const complement = toText(address.complement)
  const zipcode = toText(address.zipcode)
  const neighborhood = toText(address.neighborhood)
  const city = toText(address.city)
  const uf = toText(address.uf)

  const locality = [
    neighborhood ? t('receipt-model.commission_info.address_neighborhood', { neighborhood }) : '',
    city,
    uf,
  ].filter(Boolean).join(', ')

  const formatted = [
    [street, number].filter(Boolean).join(', '),
    complement,
    zipcode ? t('receipt-model.commission_info.address_zipcode', { zipcode }) : '',
    locality,
  ].filter(Boolean).join(' - ')

  return formatted || EMPTY_VALUE
}

const infoItems = computed(() => {
  const data = props.solfacilData

  return [
    { key: 'value', label: t('receipt-model.commission_info.value'), value: formatCommissionValue(props.commissionValue) },
    { key: 'cnpj', label: t('receipt-model.commission_info.cnpj'), value: orEmptyValue(data?.document) },
    { key: 'corporate-name', label: t('receipt-model.commission_info.corporate_name'), value: orEmptyValue(data?.corporate_name) },
    { key: 'municipal-registration', label: t('receipt-model.commission_info.municipal_registration'), value: orEmptyValue(data?.municipal_registration) },
    { key: 'main-activity', label: t('receipt-model.commission_info.main_activity'), value: orEmptyValue(data?.main_activity) },
    { key: 'address', label: t('receipt-model.commission_info.address'), value: formatAddress(data?.address) },
    { key: 'email', label: t('receipt-model.commission_info.email'), value: orEmptyValue(data?.email) },
  ]
})
</script>

<template>
  <section
    class="commission-invoice-info"
    :aria-labelledby="titleId"
    data-testid="commission-invoice-info"
  >
    <h4 :id="titleId" class="commission-invoice-info__title">
      {{ t('receipt-model.commission_info.title') }}
    </h4>

    <dl class="commission-invoice-info__grid">
      <div
        v-for="item in infoItems"
        :key="item.key"
        class="commission-invoice-info__item"
        :data-testid="`commission-invoice-info-${item.key}`"
      >
        <dt class="commission-invoice-info__label">
          {{ item.label }}
        </dt>
        <dd class="commission-invoice-info__value">
          {{ item.value }}
        </dd>
      </div>
    </dl>
  </section>
</template>

<style lang="scss" scoped>
.commission-invoice-info {
  @apply flex flex-col gap-micro mt-4xs p-4xs;
  @apply rounded-md border-a-xs border-neutral-high-medium bg-neutral-high-light;

  &__title {
    @apply m-0 fonts-body-medium-bold text-neutral-low-dark;
  }

  &__grid {
    @apply grid grid-cols-1 gap-x-4xs gap-y-micro m-0;

    @screen sm:system {
      @apply grid-cols-3;
    }
  }

  &__item {
    @apply flex flex-col gap-[2px] min-w-0;
  }

  &__label {
    @apply m-0 font-base font-bold text-[11px] text-neutral-low-light;

    line-height: theme('lineHeight.xs');
  }

  &__value {
    @apply m-0 font-base font-regular text-[13px] text-neutral-low-dark break-words;

    line-height: theme('lineHeight.lg');
  }
}
</style>
